import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { useLocalStorageState } from '../../../hooks/useLocalStorage';
import { MastheadPortalId } from '../../../models/AdminModels';
import { AgentRole } from '../../../models/Support/SupportEnums';
import { useMyAgentConfigQuery } from '../../../queries/Support/useSupportAgentConfig';
import { useSupportCategoriesQuery } from '../../../queries/Support/useSupportCategories';
import { useSupportInboxCounts } from '../../../queries/Support/useSupportInboxNav';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import { useSupportTeamQuery } from '../../../queries/Support/useSupportTeam';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { useSupportTicketSearchQuery } from '../../../queries/Support/useSupportTicketSearch';
import { useSupportTicketsQuery } from '../../../queries/Support/useSupportTicketsQuery';
import { Portal } from '../../../web/components/Portal';
import { AdminSupportPageLayout } from '../../components/supportShared/AdminSupportPageLayout';
import { NewTicketModal } from '../../components/supportShared/NewTicketModal';
import { LAST_MAILBOX_STORAGE_KEY } from '../../components/supportShared/supportStorageKeys';
import { SupportMastheadAppBar } from '../../components/supportShared/SupportMastheadAppBar';
import { ChangeCustomerModal } from './ChangeCustomerModal';
import { ConversationPane } from './ConversationPane';
import { DEFAULT_INBOX_FILTERS, type InboxFilters, type InboxView, sortToListParams } from './inboxFilters';
import { ReportsPlaceholder } from './ReportsPlaceholder';
import { RightPanel } from './RightPanel';
import { inboxStyles as s } from './styles/inboxStyles';
import { sinkPendingTickets } from './supportTicketPresentation';
import { TicketListPane, type TicketListSource } from './TicketListPane';
import { TicketTableView } from './TicketTableView';
import { TicketTopBar } from './TicketTopBar';

const PAGE_SIZE = 25;
const CATEGORY_FILTER_PREFIX = 'category:';

/** Human labels for the standard inbox filters surfaced as views. */
const FILTER_LABELS: Record<string, string> = {
	all: 'All tickets',
	unassigned: 'Unassigned',
	mine: 'My tickets',
	priority: 'Priority',
	assigned: 'Assigned',
	closed: 'Closed',
	spam: 'Spam',
};

const parsePageParam = (value: string | null) => {
	const page = Number(value);
	return Number.isInteger(page) && page > 0 ? page : 1;
};

const clearMailboxScopedFilters = (filters: InboxFilters): InboxFilters => ({
	...filters,
	categoryId: undefined,
	assigneeId: undefined,
});

export const AdminSupportInboxPage: React.FC = () => {
	const location = useLocation();
	const history = useHistory();
	const params = new URLSearchParams(location.search);
	const accountId = params.get('accountId') || undefined;
	const accountName = params.get('accountName') || undefined;
	const activeFilter = params.get('filter') || (accountId ? 'all' : 'unassigned');
	const selectedTicketId = params.get('ticketId') || undefined;
	const targetThreadId = params.get('threadId') || undefined;
	const currentPage = parsePageParam(params.get('page'));
	const screen = params.get('screen') === 'reports' ? 'reports' : 'inbox';

	const viewParam = params.get('view');
	const inboxView: InboxView = viewParam === 'desk' ? 'desk' : 'table';

	// Push layout transitions for browser Back; replace state changes within a layout.
	const setParams = React.useCallback(
		(updates: Record<string, string | null>, options?: { push?: boolean }) => {
			const next = new URLSearchParams(location.search);
			for (const [key, value] of Object.entries(updates)) {
				if (value === null) next.delete(key);
				else next.set(key, value);
			}
			const search = next.toString();
			if (!options?.push && search === location.search.replace(/^\?/, '')) return;
			const path = search ? `/support?${search}` : '/support';
			if (options?.push) {
				history.push(path);
			} else {
				history.replace(path);
			}
		},
		[history, location.search]
	);

	// Notification deep links omit mailboxId, so prefer the last valid mailbox before the first active one.
	const { data: mailboxes } = useSupportMailboxListQuery();
	const mailboxList = mailboxes ?? [];
	const requestedMailboxId = params.get('mailboxId');
	const [storedMailboxId, setStoredMailboxId] = useLocalStorageState<string>(LAST_MAILBOX_STORAGE_KEY, '');
	const isKnownMailbox = (id: string | null): id is string => !!id && mailboxList.some(m => m.id === id);
	const defaultMailboxId = mailboxList.find(m => m.isActive)?.id ?? mailboxList[0]?.id ?? '';
	const selectedMailboxId = isKnownMailbox(requestedMailboxId)
		? requestedMailboxId
		: isKnownMailbox(storedMailboxId)
			? storedMailboxId
			: defaultMailboxId;

	React.useEffect(() => {
		if (!selectedMailboxId) return;
		setStoredMailboxId(selectedMailboxId);
	}, [selectedMailboxId, setStoredMailboxId]);

	const navItems = useSupportInboxCounts(selectedMailboxId);
	const categoriesQuery = useSupportCategoriesQuery(selectedMailboxId);
	// Depend on the react-query data reference (stable between renders) rather than a fresh `?? []`.
	const categoryValues = categoriesQuery.data?.values;
	const categories = categoryValues ?? [];
	const categoryMap = React.useMemo(() => {
		const map: Record<string, { name: string; color?: string }> = {};
		for (const c of categoryValues ?? []) map[c.id] = { name: c.name, color: c.color };
		return map;
	}, [categoryValues]);

	const requestedCategoryView = activeFilter.startsWith(CATEGORY_FILTER_PREFIX);
	const mailboxUrlNeedsReset =
		requestedCategoryView || params.has('page') || params.has('ticketId') || params.has('threadId');

	// Free-text search and the Search criteria popover both live in local state (not the URL). A query
	// runs the server-side full-text search endpoint, scoped to the selected mailbox; with no query,
	// the same structured refinements apply to the current view via the ticket-list endpoint. Sort is
	// applied on the list endpoint — search results keep the server's recency ordering.
	const [searchInput, setSearchInput] = React.useState('');
	const [inboxFilters, setInboxFilters] = React.useState<InboxFilters>(DEFAULT_INBOX_FILTERS);
	const [searchPage, setSearchPage] = React.useState(1);
	const [settledMailboxId, setSettledMailboxId] = React.useState<string | null>(() => selectedMailboxId || null);
	const isMailboxTransition =
		!!selectedMailboxId && settledMailboxId !== null && settledMailboxId !== selectedMailboxId;
	const activeViewFilter =
		isMailboxTransition && requestedCategoryView ? (accountId ? 'all' : 'unassigned') : activeFilter;
	const isCategoryView = activeViewFilter.startsWith(CATEGORY_FILTER_PREFIX);
	const activeCategoryId = isCategoryView
		? activeViewFilter.slice(CATEGORY_FILTER_PREFIX.length) || undefined
		: undefined;
	const queryFilter = isCategoryView ? 'all' : activeViewFilter;
	const filters = isMailboxTransition ? clearMailboxScopedFilters(inboxFilters) : inboxFilters;
	const activeListPage = isMailboxTransition ? 1 : currentPage;
	const activeSearchPage = isMailboxTransition ? 1 : searchPage;
	const debouncedSearch = useDebounceValue(searchInput, 300);
	const trimmedSearch = debouncedSearch.trim();
	const isSearchDebouncing = searchInput.trim() !== trimmedSearch;
	const isTextSearch = trimmedSearch.length >= 2;
	const isSearchTransition = isSearchDebouncing && (searchInput.trim().length >= 2 || isTextSearch);
	const isResultTransition = isMailboxTransition || isSearchTransition;
	// The ticket-list filter pipeline has no CSM property (a ticket's CSM lives on its account), so an
	// active CSM filter always routes through the search endpoint, which resolves the CSM's accounts.
	const isSearching = isTextSearch || !!filters.csmId;

	const teamQuery = useSupportTeamQuery(selectedMailboxId);
	const teamMembers = teamQuery.data?.values ?? [];

	const resetResultState = React.useCallback(
		(updates: Record<string, string | null> = {}) => {
			setSearchPage(1);
			setParams({ page: null, ticketId: null, threadId: null, ...updates });
		},
		[setParams]
	);
	const changeSearch = React.useCallback(
		(value: string) => {
			setSearchInput(value);
			resetResultState();
		},
		[resetResultState]
	);
	const changeFilters = React.useCallback(
		(next: InboxFilters) => {
			setInboxFilters(next);
			resetResultState();
		},
		[resetResultState]
	);
	const clearFilters = React.useCallback(() => {
		setInboxFilters(DEFAULT_INBOX_FILTERS);
		resetResultState();
	}, [resetResultState]);

	React.useLayoutEffect(() => {
		if (!selectedMailboxId) return;
		if (settledMailboxId === null) {
			setSettledMailboxId(selectedMailboxId);
			return;
		}
		if (settledMailboxId === selectedMailboxId) return;

		setInboxFilters(current =>
			current.categoryId || current.assigneeId ? clearMailboxScopedFilters(current) : current
		);
		setSearchPage(1);

		if (mailboxUrlNeedsReset) {
			setParams({
				page: null,
				ticketId: null,
				threadId: null,
				...(requestedCategoryView ? { filter: null } : {}),
			});
			return;
		}

		setSettledMailboxId(selectedMailboxId);
	}, [mailboxUrlNeedsReset, requestedCategoryView, selectedMailboxId, setParams, settledMailboxId]);

	const listSort = sortToListParams(filters.sort);
	const ticketsQuery = useSupportTicketsQuery({
		filter: queryFilter,
		mailboxId: selectedMailboxId,
		accountId,
		categoryId: filters.categoryId ?? (isMailboxTransition ? undefined : activeCategoryId),
		status: filters.status,
		priority: filters.priority,
		assigneeId: filters.assigneeId,
		contact: filters.contact,
		sort: listSort.sort,
		sortBy: listSort.sortBy,
		page: activeListPage,
		pageSize: PAGE_SIZE,
		enabled: !isSearching && !isResultTransition,
	});
	const tickets = ticketsQuery.data?.values ?? [];
	const totalCount = ticketsQuery.data?.totalCount ?? 0;
	const totalPages = Math.ceil(totalCount / PAGE_SIZE);

	// Search takes the same id-based refinements as the list endpoint and composes them with the
	// full-text query server-side.
	const searchQuery = useSupportTicketSearchQuery(
		isSearching && !isResultTransition
			? {
					query: isTextSearch ? trimmedSearch : '',
					mailboxId: selectedMailboxId,
					accountId,
					status: filters.status,
					priority: filters.priority,
					categoryId: filters.categoryId,
					assigneeId: filters.assigneeId,
					csmId: filters.csmId,
					page: activeSearchPage,
					pageSize: PAGE_SIZE,
				}
			: { query: '', page: 1, pageSize: PAGE_SIZE }
	);

	const myAgentConfigQuery = useMyAgentConfigQuery(selectedMailboxId);
	const canManageSettings = myAgentConfigQuery.data?.role === AgentRole.Admin;
	// Wait for membership to load; non-agent writes return 403.
	const isNotAnAgent = myAgentConfigQuery.data?.isAgent === false;

	const selectFilter = (key: string) => resetResultState({ filter: key });
	const selectAccount = (account: { id?: string; companyName?: string }) => {
		if (!account.id) return;
		resetResultState({
			accountId: account.id,
			accountName: account.companyName || null,
			filter: 'all',
		});
	};
	const clearAccount = () => {
		resetResultState({ accountId: null, accountName: null, filter: null });
	};
	const selectMailbox = (id: string) => {
		setInboxFilters(current => clearMailboxScopedFilters(current));
		setSearchPage(1);
		setParams({
			mailboxId: id,
			page: null,
			ticketId: null,
			threadId: null,
			...(requestedCategoryView ? { filter: null } : {}),
		});
	};

	const selectTicket = (id: string) => setParams({ ticketId: id, threadId: null });
	const selectListPage = (page: number) => {
		setParams({ page: page === 1 ? null : String(page), ticketId: null, threadId: null });
	};
	const selectSearchPage = (page: number) => {
		setSearchPage(page);
		setParams({ page: null, ticketId: null, threadId: null });
	};

	// The mailbox renders from search results while a query is active, else from the filter view.
	const listTickets = isResultTransition ? [] : isSearching ? (searchQuery.data?.values ?? []) : tickets;
	// Desk pages float active tickets above pending ones; the table keeps the server order.
	const orderedTickets = inboxView === 'desk' ? sinkPendingTickets(listTickets) : listTickets;

	// Search results carry their own pagination (searchPage); the filter view is paged via the URL.
	const list: TicketListSource = isSearching
		? {
				tickets: orderedTickets,
				isLoading: isResultTransition || searchQuery.isLoading,
				isError: !isResultTransition && searchQuery.isError,
				totalCount: isResultTransition ? 0 : (searchQuery.data?.totalCount ?? 0),
				page: activeSearchPage,
				totalPages: isResultTransition ? 0 : Math.ceil((searchQuery.data?.totalCount ?? 0) / PAGE_SIZE),
				onSelectPage: selectSearchPage,
			}
		: {
				tickets: orderedTickets,
				isLoading: isResultTransition || ticketsQuery.isLoading,
				isError: !isResultTransition && ticketsQuery.isError,
				totalCount: isResultTransition ? 0 : totalCount,
				page: activeListPage,
				totalPages: isResultTransition ? 0 : totalPages,
				onSelectPage: selectListPage,
			};

	const firstTicketId = orderedTickets[0]?.id;
	const hasSettledActiveResults =
		!isMailboxTransition &&
		!isSearchDebouncing &&
		!list.isLoading &&
		!list.isError &&
		(list.totalPages === 0 || list.page <= list.totalPages);
	const stableSelectedTicketId = isMailboxTransition ? undefined : selectedTicketId;
	// Render the first settled result immediately while the URL-sync effect catches up. An explicit
	// deep-link selection always wins, including while its surrounding result list is still loading.
	const activeTicketId =
		stableSelectedTicketId ??
		(screen === 'inbox' && inboxView === 'desk' && hasSettledActiveResults ? firstTicketId : undefined);

	const { data: selectedTicket } = useSupportTicketQuery(activeTicketId || '');
	// Bumped when the user picks Forward from the ticket bar; ConversationPane watches it to open
	// the composer in forward mode (the composer state lives in that pane, not here).
	const [forwardSignal, setForwardSignal] = React.useState(0);
	const [showChangeCustomerModal, setShowChangeCustomerModal] = React.useState(false);
	const [showNewTicketModal, setShowNewTicketModal] = React.useState(false);

	// After close/spam/assign in the desk view, move to the neighboring ticket instead of staying on
	// one that just left the queue; the list refetch then drops the old row without emptying the pane.
	const advanceFromTicket = React.useCallback(
		(fromTicketId: string) => {
			const index = orderedTickets.findIndex(ticket => ticket.id === fromTicketId);
			if (index < 0) return;
			const next = orderedTickets[index + 1] ?? orderedTickets[index - 1];
			setParams({ ticketId: next ? next.id : null, threadId: null });
		},
		[orderedTickets, setParams]
	);

	const openTicketFromTable = (id: string) => setParams({ view: 'desk', ticketId: id, threadId: null }, { push: true });

	const showTableView = () => setParams({ view: null, ticketId: null, threadId: null }, { push: true });

	// Auto-select the first ticket so the conversation/details panes are populated on landing.
	// The table view has no selection to sync.
	React.useEffect(() => {
		if (screen !== 'inbox') return;
		if (inboxView !== 'desk') return;
		if (selectedTicketId) return;
		if (!hasSettledActiveResults) return;
		if (!firstTicketId) return;
		setParams({ ticketId: firstTicketId, threadId: null });
	}, [screen, inboxView, selectedTicketId, hasSettledActiveResults, firstTicketId, setParams]);

	React.useEffect(() => {
		if (screen !== 'inbox') return;
		if (isSearchDebouncing) return;
		if (list.totalPages <= 0) return;
		if (list.page <= list.totalPages) return;
		if (isSearching) {
			setSearchPage(list.totalPages);
			setParams({ page: null, ticketId: null, threadId: null });
			return;
		}
		setParams({ page: list.totalPages === 1 ? null : String(list.totalPages), ticketId: null, threadId: null });
	}, [screen, isSearchDebouncing, isSearching, list.page, list.totalPages, setParams]);

	React.useEffect(() => {
		setShowChangeCustomerModal(false);
	}, [activeTicketId]);

	const activeNavItem = navItems.find(item => item.key === activeViewFilter);
	const accountFilterLabel = accountName ? `${accountName} tickets` : 'Account tickets';
	const filterLabel = isCategoryView
		? (categoryMap[activeViewFilter.slice(CATEGORY_FILTER_PREFIX.length)]?.name ?? 'Category')
		: accountId && activeViewFilter === 'all'
			? accountFilterLabel
			: (activeNavItem?.label ?? FILTER_LABELS[activeViewFilter] ?? 'Inbox');
	// A CSM-only refinement is still served by the search endpoint but reads better as "filtered".
	const viewLabel = isSearching ? (isTextSearch ? 'Search results' : 'Filtered results') : filterLabel;

	const gridCols = '300px minmax(420px, 1fr) auto';
	const settingsPath = selectedMailboxId
		? `/support/settings?mailboxId=${encodeURIComponent(selectedMailboxId)}`
		: '/support/settings';

	return (
		<AdminSupportPageLayout>
			<Portal destination={MastheadPortalId}>
				<SupportMastheadAppBar
					activeTab={screen}
					mailboxes={mailboxList}
					selectedMailboxId={selectedMailboxId}
					onSelectInbox={() => setParams({ screen: null })}
					onSelectReports={() => setParams({ screen: 'reports' })}
					onSelectSettings={() => history.push(settingsPath)}
					onSelectMailbox={selectMailbox}
					onNewTicket={isNotAnAgent ? undefined : () => setShowNewTicketModal(true)}
					showMailboxSwitcher={screen !== 'inbox' || inboxView === 'table'}
					showSettings={canManageSettings}
				>
					{screen === 'inbox' && inboxView === 'desk' && activeTicketId && selectedTicket && (
						<TicketTopBar
							ticket={selectedTicket}
							onForward={() => setForwardSignal(value => value + 1)}
							onChangeCustomer={() => setShowChangeCustomerModal(true)}
							onTicketLeftView={() => advanceFromTicket(selectedTicket.id)}
						/>
					)}
				</SupportMastheadAppBar>
			</Portal>

			{screen === 'reports' ? (
				<ReportsPlaceholder mailboxName={mailboxList.find(m => m.id === selectedMailboxId)?.name} />
			) : (
				<div className={css(s.desk)}>
					<div className={css(s.deskColumn)}>
						{isNotAnAgent && (
							<div className={css(s.notAgentNotice)} role='status'>
								You're not an agent in this mailbox, so actions like replying, adding notes, and assigning tickets will
								be denied. Ask a mailbox admin to add you to the team.
							</div>
						)}
						<div className={css(s.deskBody)}>
							{inboxView === 'table' ? (
								<TicketTableView
									list={list}
									navItems={navItems}
									activeFilter={activeViewFilter}
									onSelectFilter={selectFilter}
									isSearching={isSearching}
									searchValue={searchInput}
									onSearchChange={changeSearch}
									filters={filters}
									onFiltersChange={changeFilters}
									onClearFilters={clearFilters}
									categories={categories}
									teamMembers={teamMembers}
									categoryMap={categoryMap}
									showAccountColumn={!accountId}
									accountId={accountId}
									accountName={accountName}
									onSelectAccount={selectAccount}
									onClearAccount={clearAccount}
									onOpenTicket={openTicketFromTable}
								/>
							) : (
								<div className={css(s.paneGrid)} style={{ gridTemplateColumns: gridCols }}>
									<TicketListPane
										list={list}
										viewLabel={viewLabel}
										onBack={showTableView}
										selectedTicketId={activeTicketId}
										onSelectTicket={selectTicket}
										navItems={navItems}
										categories={categories}
										activeFilter={activeViewFilter}
										onSelectFilter={selectFilter}
										isSearching={isSearching}
										searchValue={searchInput}
										onSearchChange={changeSearch}
										filters={filters}
										onFiltersChange={changeFilters}
										onClearFilters={clearFilters}
										teamMembers={teamMembers}
										categoryMap={categoryMap}
									/>
									{activeTicketId ? (
										<ConversationPane
											ticketId={activeTicketId}
											forwardSignal={forwardSignal}
											targetThreadId={targetThreadId}
											onChangeCustomer={() => setShowChangeCustomerModal(true)}
											onTicketClosed={() => advanceFromTicket(activeTicketId)}
										/>
									) : (
										<div className={css(s.conversationPane)}>
											<div className={css(s.empty)}>Select a ticket to open the conversation.</div>
										</div>
									)}
									{activeTicketId ? (
										<RightPanel
											ticketId={activeTicketId}
											defaultTab='details'
											onSelectTicket={selectTicket}
											onChangeCustomer={() => setShowChangeCustomerModal(true)}
											onTicketLeftView={() => advanceFromTicket(activeTicketId)}
										/>
									) : (
										<aside className={css(s.supportDrawerPlaceholder)}>
											<div className={css(s.empty)}>Select a ticket to see details.</div>
										</aside>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
			{showChangeCustomerModal && activeTicketId && (
				<ChangeCustomerModal
					ticketId={activeTicketId}
					hasCustomer={!!selectedTicket?.contactEmail?.trim()}
					onClose={() => setShowChangeCustomerModal(false)}
				/>
			)}
			{showNewTicketModal && (
				<NewTicketModal
					defaultAccount={accountId ? { id: accountId, name: accountName } : undefined}
					defaultMailboxId={selectedMailboxId}
					onClose={() => setShowNewTicketModal(false)}
					onCreated={ticket =>
						setParams({ screen: null, view: 'desk', ticketId: ticket.id, threadId: null }, { push: true })
					}
				/>
			)}
		</AdminSupportPageLayout>
	);
};
